import { BranchesOutlined } from "@ant-design/icons";
import { Button, Space, Table, Tooltip } from "antd";
import Avatar from "antd/lib/avatar/avatar";
import { useOutletContext } from "react-router";
import { Link } from "react-router-dom";
import DeploymentStatusTag from "../../components/DeploymentStatusTag";
import CardMarketing from "../../components/marketing/CardMarketing";
import { Dictionary, IResourceDetails } from "../../types";
import DeploymentsImage from "../../assets/deployment-history.png";
import { useRecoilValue } from "recoil";
import { currentProjectState } from "../../recoil/project";
import { fetchProjectLimitations } from "../../utils/local-storage";

export default function ResourceDeployments() {
    const currentProject = useRecoilValue(currentProjectState);
    const { resourceDetails } = useOutletContext<{ resourceDetails: IResourceDetails }>();
    const limitations = fetchProjectLimitations();

    const isTrialEligible = (planId: string) => {
        return currentProject?.freeTrials && !(currentProject.freeTrials as Dictionary<boolean>)[planId];
    }

    return (
        <>
            {
                !limitations.deploymentInsights ?
                    <CardMarketing
                        title="Deployment History"
                        subtitle="Deployment history is available in Advanced Plan ($99/month)."
                        description="Know the state of your apps and infrastructure at any point in time. Follow deployments per environment in real-time, understand who did the changes and drill down to the exact commit."
                        action={
                            <Button>
                                <Link to="/pricing/upgrade">
                                    {isTrialEligible("ADVANCED") ? "Start 7 day trial" : "Upgrade to Advanced Plan"}
                                </Link>
                            </Button>
                        }
                        image={DeploymentsImage}
                        imageShadow={true}
                    /> :
                    <Table
                        showHeader={false}
                        pagination={false}
                        rowKey="id"
                        className="ant-table-wrapper-bordered"
                        expandable={{
                            rowExpandable: record => !!record.status.errors,
                            expandedRowRender: (record: any) => {
                                return (
                                    <div style={{ margin: "-16px" }}>
                                        <Table
                                            title={() =>
                                                <div className="flex-justify-space-between">
                                                    This deployment failed with errors.
                                                    <DeploymentStatusTag status={record.status.name} />
                                                </div>
                                            }
                                            dataSource={record.status.errors}
                                            columns={[
                                                {
                                                    title: "Resource ID",
                                                    dataIndex: "logicalResourceId"
                                                },
                                                {
                                                    title: "Resource Type",
                                                    dataIndex: "resourceType"
                                                },
                                                {
                                                    title: "Reason",
                                                    dataIndex: "statusReason"
                                                }
                                            ]}
                                            pagination={false}
                                            size="small"
                                        />
                                    </div>
                                )
                            }
                        }}
                        columns={[
                            {
                                title: 'Id',
                                key: 'deployment',
                                render: deployment =>
                                    <Tooltip placement="topLeft" title={deployment.version}>
                                        <b style={{ color: deployment.status.errors ? "red" : "" }}>
                                            {deployment.commitType === "branch" ? deployment.version.substring(0, 7) : deployment.version}
                                        </b>
                                    </Tooltip>
                            },
                            {
                                title: 'Commit',
                                key: 'commitMessage',
                                render: deployment => <>
                                    <div>
                                        {deployment.commitMessage}
                                    </div>

                                    {deployment.createdAt} on <BranchesOutlined /> {deployment.branch}
                                </>,
                            },
                            {
                                title: 'Commit User',
                                key: 'createdBy',
                                align: "right",
                                render: deployment => <>
                                    <Space>
                                        {deployment.timestamp}
                                        <Tooltip title={deployment.initiator.name}>
                                            <Avatar src={deployment.initiator.avatar} size="small" />
                                        </Tooltip>
                                    </Space>
                                </>,
                            },
                            Table.EXPAND_COLUMN
                        ]}
                        dataSource={resourceDetails.deployments}
                    />
            }
        </>
    )
}